import { keyframes } from "tss-react";
import { makeStyles } from "tss-react/mui";

import { actionPrimary, actionSecondary } from "@/shared/styles/action.styles";

const REDUCED_MOTION = "@media (prefers-reduced-motion: reduce)";
const FULL_MOTION = "@media (prefers-reduced-motion: no-preference)";

const riseIn = keyframes({
  from: { opacity: 0, transform: "translateY(12px)" },
  to: { opacity: 1, transform: "none" },
});

const coinFlip = keyframes({
  from: { opacity: 0, transform: "rotateY(-180deg) scale(0.6)" },
  to: { opacity: 1, transform: "rotateY(0deg) scale(1)" },
});

const slideFromLeft = keyframes({
  from: { opacity: 0, transform: "translateX(-0.25em)" },
  to: { opacity: 1, transform: "none" },
});

const slideFromRight = keyframes({
  from: { opacity: 0, transform: "translateX(0.25em)" },
  to: { opacity: 1, transform: "none" },
});

const shine = keyframes({
  from: { backgroundPosition: "160% 0, 0 0" },
  to: { backgroundPosition: "-60% 0, 0 0" },
});

const styles = makeStyles()((theme) => {
  const { color, gradient, motion, radius, space, typography } = theme.design;

  return {
    root: {
      display: "flex",
      flexDirection: "column",
      minHeight: "100vh",
      boxSizing: "border-box",
      padding: `${space.xl}px ${space["2xl"]}px`,
      fontFamily: typography.fontFamily,
      backgroundColor: color.surface.canvas,
      backgroundImage: "url(/assets/imgs/map/city_selection_bg.png)",
      backgroundRepeat: "no-repeat",
      backgroundSize: "100% auto",
      backgroundPosition: "center top",

      [theme.breakpoints.down("sm")]: {
        padding: `${space.lg}px`,
        backgroundSize: "auto 360px",
      },
    },

    brand: {
      display: "inline-flex",
      alignItems: "center",
      alignSelf: "flex-start",
      gap: space.md,
      color: color.text.primary,
      textDecoration: "none",
      borderRadius: radius.md,

      "&:focus-visible": {
        outline: "none",
        boxShadow: theme.design.shadow.focus,
      },
    },

    brandLogo: {
      width: 48,
      height: 48,
      borderRadius: "50%",
    },

    brandName: {
      fontSize: 24,
      fontWeight: 700,
      letterSpacing: 1,
    },

    content: {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: space.lg,
      width: "100%",
      maxWidth: theme.design.size.content.narrow,
      margin: "0 auto",
      padding: `${space["3xl"]}px 0`,
      textAlign: "center",
      animation: `${riseIn} ${motion.duration.slow} ${motion.easing.standard} both`,

      [REDUCED_MOTION]: { animation: "none" },
    },

    code: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "0.08em",
      marginBottom: space.sm,
      fontFamily: typography.fontFamily,
      fontWeight: 700,
      fontSize: 200,
      lineHeight: 1,
      perspective: 800,
      userSelect: "none",

      [theme.breakpoints.down("sm")]: {
        fontSize: 120,
      },
    },

    digit: {
      display: "block",
      backgroundImage: `${gradient.text.medallionShine}, ${gradient.text.medallion}`,
      backgroundSize: "250% 100%, 100% 100%",
      backgroundPosition: "-60% 0, 0 0",
      backgroundRepeat: "no-repeat",
      backgroundClip: "text",
      WebkitBackgroundClip: "text",
      color: color.overlay.transparent,
      filter: `drop-shadow(${theme.design.shadow.medallionText})`,
      animation: `${shine} 1.4s ${motion.easing.standard} 0.9s both`,

      [REDUCED_MOTION]: { animation: "none" },
    },

    digitLeft: {
      [FULL_MOTION]: {
        animation: `${slideFromLeft} 0.6s ${motion.easing.standard} 0.15s both, ${shine} 1.4s ${motion.easing.standard} 0.9s both`,
      },
    },

    digitRight: {
      [FULL_MOTION]: {
        animation: `${slideFromRight} 0.6s ${motion.easing.standard} 0.15s both, ${shine} 1.4s ${motion.easing.standard} 0.9s both`,
      },
    },

    medallion: {
      position: "relative",
      display: "block",
      width: "0.8em",
      height: "0.8em",
      isolation: "isolate",
      animation: `${coinFlip} 0.9s ${motion.easing.emphasized} both`,

      "&::before": {
        content: '""',
        position: "absolute",
        inset: "-45%",
        zIndex: -1,
        background: gradient.surface.medallionHalo,
        pointerEvents: "none",
      },

      [REDUCED_MOTION]: { animation: "none" },
    },

    medallionImage: {
      display: "block",
      width: "100%",
      height: "100%",
      borderRadius: "50%",
      objectFit: "contain",
      boxShadow: theme.design.shadow.medallion,
    },

    title: {
      fontFamily: typography.fontFamily,
      fontWeight: 700,
      fontSize: 36,
      lineHeight: typography.lineHeight.compact,
      color: color.text.primary,

      [theme.breakpoints.down("sm")]: {
        fontSize: 28,
      },
    },

    description: {
      maxWidth: theme.design.layout.readableLineLength,
      fontFamily: typography.fontFamily,
      fontSize: typography.size.body,
      lineHeight: typography.lineHeight.default,
      color: color.text.muted,
    },

    path: {
      fontFamily: typography.fontFamily,
      fontWeight: 700,
      color: color.text.primary,
      overflowWrap: "anywhere",
    },

    actions: {
      display: "flex",
      flexWrap: "wrap",
      justifyContent: "center",
      gap: space.md,
      marginTop: space.sm,
    },

    primary: {
      ...actionPrimary(theme),
      minWidth: 160,
      minHeight: theme.design.size.control.lg,
      padding: `0 ${space.xl}px`,
      fontSize: typography.size.body,
    },

    secondary: {
      ...actionSecondary(theme),
      minWidth: 160,
      minHeight: theme.design.size.control.lg,
      padding: `0 ${space.xl}px`,
      fontSize: typography.size.body,
    },
  };
});

export default styles;
